import { useNavigation } from '@react-navigation/native';
import React, { useMemo } from 'react';
import {
  Image,
  Linking,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';

import Avatar from '@components/Avatar/Avatar';
import Icon from '@components/shared/Icon/Icon';
import { File, useGetProjectQuery } from '@graphql/generated';
import useMe from '@hooks/useMe';
import { OnboardingProject } from '@hooks/useProjectOnboarding';
import { buildAvatarUri } from '@screens/Projects/ProjectInvitationPreviewScreen.web';
import Images from '@themes/images';
import theme from '@themes/theme';

import {
  getAvatarColor,
  getInitials,
  truncateName,
} from './projectInvitationPreviewShared';
import { GreyIconBox, sharedStyleDefs } from './projectPreviewShared';

const PLAY_STORE_URL =
  'https://play.google.com/store/apps/details?id=com.eloveit.TaskTag';
const APP_STORE_URL = 'https://apps.apple.com/app/id6448815209';

function getAppStoreUrl(): string {
  if (typeof window === 'undefined') return PLAY_STORE_URL;
  const ua = window.navigator.userAgent;
  return /iPhone|iPad|iPod/i.test(ua) ? APP_STORE_URL : PLAY_STORE_URL;
}

/* ─── main component ───────────────────────────────────────────────────────── */

interface Props {
  project: OnboardingProject;
}

export const ProjectWelcomeMobileScreen: React.FC<Props> = ({ project }) => {
  const navigation = useNavigation<any>();
  const { me } = useMe();

  const goToProfile = () =>
    navigation.navigate('my-account', { screen: 'my-profile-mobile' });

  const { data } = useGetProjectQuery({
    variables: { id: project.uuid },
    fetchPolicy: 'cache-first',
  });

  const detail = data?.getProject;

  const projectIconName = (
    detail?.icon && detail.icon in Images ? detail.icon : 'IconStart22'
  ) as React.ComponentProps<typeof Icon>['name'];

  const teamAvatarUri = buildAvatarUri(detail?.team?.avatarFile);

  const name = detail?.name ?? project.name;
  const address = detail?.address ?? null;
  const description = detail?.description ?? null;
  const usersCount = detail?.usersCount ?? 0;
  const teamName = detail?.team?.name ?? null;

  const members = useMemo(() => {
    if (!detail?.members) return [];
    // Raw useGetProjectQuery data: members[].user can be null for
    // privacy-gated rows (TA-10454), so drop unrenderable entries before the
    // owner/member avatar JSX dereferences user.
    return detail.members.filter((m) => !!m.user).slice(0, 8);
  }, [detail?.members]);

  const owner = useMemo(
    () => members.find((m) => m.role === 'OWNER') ?? members[0] ?? null,
    [members]
  );

  const handleDownloadApp = () => Linking.openURL(getAppStoreUrl());

  return (
    <View style={styles.container}>
      {/* ── Top nav bar ─────────────────────────────────────────────────── */}
      <View style={styles.topBar}>
        <Icon name='Logo' width={100} height={28} color='textPrimary' />
        <Avatar
          size='small'
          avatar={(me?.avatar as File) ?? null}
          label={me?.name}
          onPress={goToProfile}
          accessibilityLabel='My profile'
        />
      </View>

      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.content}
        showsVerticalScrollIndicator={false}>
        {/* ── 1. Project header card (purple) ─────────────────────────── */}
        <View style={styles.headerCard}>
          <View style={styles.headerTitleRow}>
            <View style={styles.projectIconBox}>
              <Icon
                name={projectIconName}
                width={24}
                height={24}
                color='white'
              />
            </View>
            <Text style={styles.projectName} numberOfLines={2}>
              {name}
            </Text>
          </View>
          {!!address && <Text style={styles.addressText}>{address}</Text>}
          <View style={styles.metaRow}>
            {!!teamName && (
              <View style={styles.metaHalf}>
                <View style={styles.teamIconBox}>
                  {teamAvatarUri ? (
                    <Image
                      source={{ uri: teamAvatarUri }}
                      style={styles.mobTeamLogoImg}
                    />
                  ) : (
                    <Icon
                      name='TTSquare'
                      width={24}
                      height={24}
                      marginTop='xxxs'
                      marginLeft='xxxs'
                    />
                  )}
                </View>
                <Text style={styles.metaText} numberOfLines={1}>
                  {truncateName(teamName, 16)}
                </Text>
              </View>
            )}
            {owner && (
              <View style={[styles.metaHalf, !teamName && styles.metaHalfFull]}>
                <View
                  style={[
                    styles.ownerAvatar,
                    { backgroundColor: getAvatarColor(0) },
                  ]}>
                  {owner.user.avatar?.cdnBaseUrl && owner.user.avatar?.path ? (
                    <Image
                      source={{
                        uri: `${owner.user.avatar.cdnBaseUrl}${owner.user.avatar.path}`,
                      }}
                      style={styles.ownerAvatarImage}
                    />
                  ) : (
                    <Text style={styles.ownerInitials}>
                      {getInitials(owner.user.name)}
                    </Text>
                  )}
                </View>
                <Text style={styles.metaText} numberOfLines={1}>
                  {owner.user.name}
                </Text>
              </View>
            )}
          </View>
        </View>

        {/* ── 2. Description ──────────────────────────────────────────── */}
        {!!description && (
          <View style={styles.card}>
            <Text style={styles.sectionTitle}>Description</Text>
            <Text style={styles.descriptionText}>{description}</Text>
          </View>
        )}

        {/* ── 3. Members ──────────────────────────────────────────────── */}
        {members.length > 0 && (
          <View style={styles.membersSection}>
            <Text style={styles.sectionTitle}>Members ({usersCount})</Text>
            <View style={styles.membersRow}>
              {members.map((member, index) => {
                const avatarUri =
                  member.user.avatar?.cdnBaseUrl && member.user.avatar?.path
                    ? `${member.user.avatar.cdnBaseUrl}${member.user.avatar.path}`
                    : null;
                return (
                  <View key={member.id} style={styles.memberItem}>
                    <View
                      style={[
                        styles.memberAvatar,
                        { backgroundColor: getAvatarColor(index) },
                      ]}>
                      {avatarUri ? (
                        <Image
                          source={{ uri: avatarUri }}
                          style={styles.memberAvatarImage}
                        />
                      ) : (
                        <Text style={styles.memberInitials}>
                          {getInitials(member.user.name)}
                        </Text>
                      )}
                    </View>
                    <Text style={styles.memberName}>
                      {truncateName(member.user.name)}
                    </Text>
                  </View>
                );
              })}
            </View>
          </View>
        )}

        {/* ── 4. Feature cards (disabled) ─────────────────────────────── */}
        <View
          style={[styles.featureCard, { cursor: 'not-allowed' } as any]}
          pointerEvents='none'>
          <GreyIconBox iconName='FileText' />
          <View style={styles.featureRowContent}>
            <Text style={styles.featureTitleDisabled}>Checklist</Text>
            <Text style={styles.featureSubtitle}>
              Start faster with a project template.
            </Text>
          </View>
        </View>

        <View
          style={[styles.featureCard, { cursor: 'not-allowed' } as any]}
          pointerEvents='none'>
          <GreyIconBox iconName='Hash' />
          <View style={styles.featureRowContent}>
            <Text style={styles.featureTitleDisabled}>Tasks</Text>
          </View>
        </View>

        <View style={styles.featureGrid} pointerEvents='none'>
          <View
            style={[
              styles.featureGridCell,
              { marginRight: theme.spacing.s, cursor: 'not-allowed' } as any,
            ]}>
            <GreyIconBox iconName='Zap' size={36} />
            <Text
              style={[
                styles.featureTitleDisabled,
                { marginTop: theme.spacing.m },
              ]}>
              Activity Log
            </Text>
          </View>
          <View
            style={[styles.featureGridCell, { cursor: 'not-allowed' } as any]}>
            <GreyIconBox iconName='FileImage' size={36} />
            <Text
              style={[
                styles.featureTitleDisabled,
                { marginTop: theme.spacing.m },
              ]}>
              Files &amp; Media
            </Text>
          </View>
        </View>

        {/* Bottom padding so fixed banner doesn't cover content */}
        <View style={{ height: 120 }} />
      </ScrollView>

      {/* ── Fixed bottom "Download the App" banner ──────────────────────────── */}
      <View style={styles.downloadBanner}>
        <Text style={styles.downloadBannerTitle}>You're all set.</Text>
        <Text style={styles.downloadBannerBody}>
          Download the app to see your jobs, message the crew, and stay on top
          of every task.
        </Text>
        <TouchableOpacity
          style={styles.downloadBannerButton}
          onPress={handleDownloadApp}
          activeOpacity={0.85}>
          <Text style={styles.downloadBannerButtonLabel}>Download the App</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
};

/* ─── styles ────────────────────────────────────────────────────────────────── */

const styles = StyleSheet.create({
  ...sharedStyleDefs,

  container: {
    position: 'fixed' as any,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: theme.colors.white,
    zIndex: 999,
  },
  content: {
    padding: theme.spacing.m,
  },
  headerCard: {
    backgroundColor: theme.colors.purple,
    borderRadius: theme.borderRadii.xs,
    padding: theme.spacing.m,
    marginBottom: theme.spacing.s,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.textPrimary,
    marginBottom: theme.spacing.s,
  },

  /* Download banner */
  downloadBanner: {
    position: 'absolute' as any,
    bottom: theme.spacing.m,
    left: theme.spacing.m,
    right: theme.spacing.m,
    backgroundColor: theme.colors.black,
    borderRadius: theme.borderRadii.s,
    padding: theme.spacing.m,
  },
  downloadBannerTitle: {
    color: theme.colors.white,
    fontSize: 16,
    fontWeight: '600',
    lineHeight: 21,
    marginBottom: 4,
  },
  downloadBannerBody: {
    color: theme.colors.white,
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 16,
    letterSpacing: 0.02,
    marginBottom: theme.spacing.m,
  },
  downloadBannerButton: {
    backgroundColor: theme.colors.greenSecondary,
    borderRadius: theme.borderRadii.xs,
    height: 48,
    alignItems: 'center',
    justifyContent: 'center',
  },
  downloadBannerButtonLabel: {
    color: theme.colors.white,
    fontSize: 16,
    fontWeight: '600',
  },
});
